import {
  ActivityOptions,
  ActivityOrderBy,
  DateTypeFilter,
  getBaseColumnsForWorkspace,
  GlobalAdminColumns,
  Severity,
  useGroups,
  useStatefulWorkspaceRoutines,
  useWorkspaceLabels,
  WorkspaceType,
} from '@gripp/shared-logic';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Colors } from '../../themes';
import { severityToVariant } from '../crop-circle/Components/Type/severityMapping';
import { TypeIcon } from '../crop-circle/Components/Type/TypeIcon';
import { Icon } from '../crop-circle/icons';
import { colors as CropCircleColors, tokens } from '../crop-circle/tokens';
import { DateRangeSelector } from '../forms/datePicker/dateRangeSelector';
import type { FilterSectionType } from './filterSectionTypes';

export type FilterContentProps = {
  variant: 'activity' | 'asset';
  sections: readonly FilterSectionType[];
  control: Control<any>;
  setValue?: UseFormSetValue<any>;
  selectedDateType?: string;
  workspaceId?: string;
  isGlobalAdmin: boolean;
  isOEM?: boolean;
};

export function FilterContent({
  variant,
  sections,
  control,
  setValue,
  selectedDateType = '',
  workspaceId,
  isGlobalAdmin,
  isOEM = false,
}: FilterContentProps) {
  const { t } = useTranslation();
  const { groups = [] } = useGroups(isGlobalAdmin ? undefined : workspaceId);
  const { labels = [] } = useWorkspaceLabels(
    isGlobalAdmin ? undefined : workspaceId
  );
  const workspaceRoutines = useStatefulWorkspaceRoutines(workspaceId);

  const has = (key: FilterSectionType) => sections.includes(key);

  // ---- Activity-only options ----
  const activityOptions = [
    {
      value: ActivityOptions.EVERYTHING,
      label: t('activity.filter.all'),
      icon: null,
    },
    {
      value: ActivityOptions.MENTIONED,
      label: t('activity.filter.mentions'),
      icon: 'At',
    },
    {
      value: ActivityOptions.SCHEDULED,
      label: t('activity.filter.scheduled'),
      icon: 'Scheduled',
    },
    {
      value: ActivityOptions.NOT_SCHEDULED,
      label: t('activity.filter.notScheduled'),
      icon: 'Not Scheduled',
    },
    {
      value: ActivityOptions.PINNED,
      label: t('activity.filter.pinned'),
      icon: 'Pin',
    },
  ];
  const dateOptions = [
    { value: DateTypeFilter.ALL, label: t('activity.filter.all') },
    { value: DateTypeFilter.TODAY, label: t('activity.filter.today') },
    { value: DateTypeFilter.THISWEEK, label: t('activity.filter.thisWeek') },
    { value: DateTypeFilter.THISMONTH, label: t('activity.filter.thisMonth') },
    {
      value: DateTypeFilter.CUSTOMDATE,
      label: t('activity.filter.customDate'),
    },
  ];
  const sortOptions = [
    {
      value: ActivityOrderBy.MostRecentUpdated,
      label: t('activity.sort.mostRecent'),
    },
    {
      value: ActivityOrderBy.ReminderDate,
      label: t('activity.sort.reminderDate'),
    },
    { value: ActivityOrderBy.CreationDate, label: t('activity.sort.oldest') },
  ];
  const activityTypesOrder = [
    Severity.Red,
    Severity.Yellow,
    Severity.Green,
    Severity.Purple,
    Severity.Blue,
  ];
  const getSeverityLabel = (severity: string) => {
    const map: Record<string, string> = {
      [Severity.Red]: t('activity.filter.urgent'),
      [Severity.Yellow]: t('activity.filter.pending'),
      [Severity.Green]: t('activity.filter.solved'),
      [Severity.Purple]: t('activity.filter.notes'),
      [Severity.Blue]: t('activity.filter.routines'),
    };
    return map[severity] ?? severity;
  };
  const onPressSeverityFilter = (
    severity: string | null,
    value: (string | undefined)[] | null | undefined,
    onChange: (...event: any[]) => void
  ) => {
    if (severity === null) {
      onChange([]);
      return;
    }
    let newValue: (string | undefined)[];
    if (!value || value.length === 0) newValue = [severity];
    else if (!value.includes(severity)) newValue = value.concat(severity);
    else newValue = value.filter((v) => v !== severity);
    onChange(newValue);
  };
  const isAllSeveritySelected = (
    value: (string | undefined)[] | null | undefined
  ) => !value || value.length === 0;

  // ---- Asset-only: base columns ----
  const baseColumns = getBaseColumnsForWorkspace(
    isOEM ? WorkspaceType.Oem : undefined
  );

  return (
    <>
      {/* ACTIVITY TYPES (activity only) */}
      {variant === 'activity' && has('activityTypes') && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('activity.filter.activityTypes')}
          </Text>
          <Controller
            control={control}
            name="severity"
            render={({ field: { onChange, value } }) => {
              const allSelected = isAllSeveritySelected(value);
              return (
                <View style={sharedStyles.optionsContainer}>
                  <Pressable
                    onPress={() => onPressSeverityFilter(null, value, onChange)}
                    style={[
                      sharedStyles.optionChip,
                      allSelected && sharedStyles.optionChipSelected,
                    ]}
                  >
                    <Text
                      style={[
                        sharedStyles.optionChipText,
                        allSelected && sharedStyles.optionChipTextSelected,
                      ]}
                    >
                      {t('activity.filter.all')}
                    </Text>
                  </Pressable>
                  {activityTypesOrder.map((severity) => {
                    const isSelected = value?.includes(severity);
                    const variantStyle = severityToVariant[severity];
                    return (
                      <Pressable
                        key={severity}
                        onPress={() =>
                          onPressSeverityFilter(severity, value, onChange)
                        }
                        style={[
                          sharedStyles.optionChip,
                          isSelected && sharedStyles.optionChipSelected,
                        ]}
                      >
                        <TypeIcon variant={variantStyle} size={16} />
                        <Text
                          style={[
                            sharedStyles.optionChipText,
                            isSelected && sharedStyles.optionChipTextSelected,
                          ]}
                        >
                          {getSeverityLabel(severity)}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              );
            }}
          />
        </View>
      )}

      {/* OPTIONS (activity only) */}
      {variant === 'activity' && has('options') && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('activity.filter.options')}
          </Text>
          <Controller
            control={control}
            name="activityOption"
            render={({ field: { onChange, value } }) => (
              <View style={sharedStyles.optionsContainer}>
                {activityOptions.map((option) => {
                  const isSelected = value === option.value;
                  return (
                    <Pressable
                      key={option.value}
                      onPress={() => onChange(option.value)}
                      style={[
                        sharedStyles.optionChip,
                        isSelected && sharedStyles.optionChipSelected,
                      ]}
                    >
                      {option.icon && (
                        <Icon
                          name={option.icon as any}
                          size={16}
                          color={isSelected ? Colors.white : Colors.black}
                        />
                      )}
                      <Text
                        style={[
                          sharedStyles.optionChipText,
                          isSelected && sharedStyles.optionChipTextSelected,
                        ]}
                      >
                        {option.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            )}
          />
        </View>
      )}

      {/* GROUPS (activity: groupIds; asset: groups) */}
      {has('groups') && (
        <View style={sharedStyles.section}>
          <View style={sharedStyles.sectionHeader}>
            <Text style={[sharedStyles.sectionTitle, { marginBottom: 0 }]}>
              {variant === 'activity'
                ? t('activity.filter.groups')
                : t('assets.filter.groups')}
            </Text>
            <Controller
              control={control}
              name="isCombinedGroupsFilter"
              render={({ field: { value, onChange } }) => (
                <Pressable
                  onPress={() => onChange(!value)}
                  style={[
                    sharedStyles.combinedFilterToggle,
                    !!value && sharedStyles.combinedFilterToggleActive,
                  ]}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: !!value }}
                  accessibilityLabel={
                    variant === 'activity'
                      ? t('activity.filter.combinedGroups')
                      : t('assets.filter.combinedGroups')
                  }
                  testID="groups-combined-filter-toggle"
                >
                  <Icon
                    name="Link"
                    size={16}
                    color={
                      value
                        ? tokens.colors.brand.white
                        : CropCircleColors.neutral[600]
                    }
                  />
                </Pressable>
              )}
            />
          </View>
          <Controller
            control={control}
            name={variant === 'activity' ? 'groupIds' : 'groups'}
            render={({ field: { onChange, value } }) => {
              const currentValue = (value || []).filter(
                (v): v is string => v != null && v !== ''
              );
              const isDefaultSelected = currentValue.length === 0;
              const onPressGroup = (groupId: string) => {
                let newValue: string[];
                if (currentValue.length === 0) newValue = [groupId];
                else if (!currentValue.includes(groupId))
                  newValue = currentValue.concat(groupId);
                else newValue = currentValue.filter((v) => v !== groupId);
                onChange(variant === 'activity' ? newValue : newValue);
              };
              return (
                <View style={sharedStyles.optionsContainer}>
                  <Pressable
                    key="groups-default"
                    onPress={() => onChange([])}
                    style={[
                      sharedStyles.optionChip,
                      isDefaultSelected && sharedStyles.optionChipSelected,
                    ]}
                  >
                    <Text
                      style={[
                        sharedStyles.optionChipText,
                        isDefaultSelected &&
                          sharedStyles.optionChipTextSelected,
                      ]}
                    >
                      {variant === 'activity'
                        ? t('activity.filter.default')
                        : t('assets.filter.default')}
                    </Text>
                  </Pressable>
                  {groups.map((group: any) => {
                    const isSelected = currentValue.includes(group.id);
                    return (
                      <Pressable
                        key={group.id}
                        onPress={() => onPressGroup(group.id)}
                        style={[
                          sharedStyles.optionChip,
                          isSelected && sharedStyles.optionChipSelected,
                        ]}
                      >
                        <Text
                          style={[
                            sharedStyles.optionChipText,
                            isSelected && sharedStyles.optionChipTextSelected,
                          ]}
                        >
                          {group.name}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              );
            }}
          />
        </View>
      )}

      {/* LABELS (activity only) */}
      {variant === 'activity' && has('labels') && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('activity.filter.labels')}
          </Text>
          <Controller
            control={control}
            name="labelIds"
            render={({ field: { onChange, value } }) => {
              const currentValue = (value || []).filter(
                (v): v is string => v != null
              );
              const isDefaultSelected = currentValue.length === 0;
              const onPressLabel = (labelId: string) => {
                let newValue: string[];
                if (currentValue.length === 0) newValue = [labelId];
                else if (!currentValue.includes(labelId))
                  newValue = currentValue.concat(labelId);
                else newValue = currentValue.filter((v) => v !== labelId);
                onChange(newValue);
              };
              return (
                <View style={sharedStyles.optionsContainer}>
                  <Pressable
                    key="labels-default"
                    onPress={() => onChange([])}
                    style={[
                      sharedStyles.optionChip,
                      isDefaultSelected && sharedStyles.optionChipSelected,
                    ]}
                  >
                    <Text
                      style={[
                        sharedStyles.optionChipText,
                        isDefaultSelected &&
                          sharedStyles.optionChipTextSelected,
                      ]}
                    >
                      {t('activity.filter.default')}
                    </Text>
                  </Pressable>
                  {labels.map((label: any) => {
                    const isSelected = currentValue.includes(label.id);
                    return (
                      <Pressable
                        key={label.id}
                        onPress={() => onPressLabel(label.id)}
                        style={[
                          sharedStyles.optionChip,
                          isSelected && sharedStyles.optionChipSelected,
                        ]}
                      >
                        <Text
                          style={[
                            sharedStyles.optionChipText,
                            isSelected && sharedStyles.optionChipTextSelected,
                          ]}
                        >
                          {label.name}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              );
            }}
          />
        </View>
      )}

      {/* DATE (activity only) */}
      {variant === 'activity' && has('date') && setValue && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('activity.filter.date')}
          </Text>
          <Controller
            control={control}
            name="dateType"
            render={({ field: { onChange, value } }) => (
              <View style={sharedStyles.optionsContainer}>
                {dateOptions.map((option) => {
                  const isSelected = value === option.value;
                  return (
                    <Pressable
                      key={option.value}
                      onPress={() => {
                        onChange(option.value);
                        if (option.value !== DateTypeFilter.CUSTOMDATE) {
                          setValue('startDate', '');
                          setValue('endDate', '');
                        }
                      }}
                      style={[
                        sharedStyles.optionChip,
                        isSelected && sharedStyles.optionChipSelected,
                      ]}
                    >
                      <Text
                        style={[
                          sharedStyles.optionChipText,
                          isSelected && sharedStyles.optionChipTextSelected,
                        ]}
                      >
                        {option.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            )}
          />
          {selectedDateType === DateTypeFilter.CUSTOMDATE && (
            <View style={sharedStyles.customDateContainer}>
              <DateRangeSelector
                control={control}
                setValue={setValue}
                styleContainer={sharedStyles.dateRangeContainer}
              />
            </View>
          )}
        </View>
      )}

      {/* SORT (activity only) */}
      {variant === 'activity' && has('sort') && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('activity.filter.sort')}
          </Text>
          <Controller
            control={control}
            name="orderBy"
            render={({ field: { onChange, value } }) => (
              <View style={sharedStyles.optionsContainer}>
                {sortOptions.map((option) => {
                  const isSelected = value === option.value;
                  return (
                    <Pressable
                      key={option.value}
                      onPress={() => onChange(option.value)}
                      style={[
                        sharedStyles.optionChip,
                        isSelected && sharedStyles.optionChipSelected,
                      ]}
                    >
                      <Text
                        style={[
                          sharedStyles.optionChipText,
                          isSelected && sharedStyles.optionChipTextSelected,
                        ]}
                      >
                        {option.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            )}
          />
        </View>
      )}

      {/* COLUMNS (asset only) */}
      {variant === 'asset' && has('columns') && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('assets.filter.columnSelection')}
          </Text>
          <Controller
            control={control}
            name="columns"
            render={({ field: { onChange, value } }) => {
              const currentValue = (value || []).filter(
                (c): c is string => c != null
              );
              const selectableBaseColumns = baseColumns.filter(
                (col) => col !== 'name'
              );
              const allColumns = Array.from(
                new Set([
                  ...selectableBaseColumns,
                  ...(isGlobalAdmin ? GlobalAdminColumns : []),
                  ...(workspaceRoutines?.map((r) => `routine:${r.id}`) || []),
                ])
              );
              const onPressColumn = (column: string) => {
                if (column === 'name') return;
                let newValue: string[];
                if (currentValue.includes(column))
                  newValue = currentValue.filter((c) => c !== column);
                else newValue = [...currentValue, column];
                if (!newValue.includes('name'))
                  newValue = ['name', ...newValue];
                onChange(newValue);
              };
              const effectiveValue = currentValue.includes('name')
                ? currentValue
                : ['name', ...currentValue];
              return (
                <View style={sharedStyles.optionsContainer}>
                  <Pressable
                    key="name"
                    disabled={true}
                    style={[
                      sharedStyles.optionChip,
                      sharedStyles.optionChipSelected,
                      sharedStyles.optionChipDisabled,
                    ]}
                  >
                    <Text
                      style={[
                        sharedStyles.optionChipText,
                        sharedStyles.optionChipTextSelected,
                      ]}
                    >
                      {t('assets.filter.columns.name')}
                    </Text>
                  </Pressable>
                  {allColumns.map((column) => {
                    const isSelected = effectiveValue.includes(column);
                    const label = column.startsWith('routine:')
                      ? workspaceRoutines?.find(
                          (r) => r.id === column.replace('routine:', '')
                        )?.name ?? column
                      : t(`assets.filter.columns.${column}`);
                    return (
                      <Pressable
                        key={column}
                        onPress={() => onPressColumn(column)}
                        style={[
                          sharedStyles.optionChip,
                          isSelected && sharedStyles.optionChipSelected,
                        ]}
                      >
                        <Text
                          style={[
                            sharedStyles.optionChipText,
                            isSelected && sharedStyles.optionChipTextSelected,
                          ]}
                        >
                          {label}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              );
            }}
          />
        </View>
      )}
    </>
  );
}

const sharedStyles = StyleSheet.create({
  section: {
    paddingTop: tokens.spacing['2xl'],
  },
  sectionTitle: {
    ...tokens.typography.label.largeEmphasised,
    fontWeight: tokens.typography.label.largeEmphasised.fontWeight,
    color: CropCircleColors.neutral[600],
    marginBottom: tokens.spacing.md,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    marginBottom: tokens.spacing.md,
  },
  combinedFilterToggle: {
    borderRadius: tokens.borderRadius.lg,
    paddingVertical: tokens.spacing.xxs,
    paddingHorizontal: tokens.spacing.xs,
    backgroundColor: CropCircleColors.neutral[200],
  },
  combinedFilterToggleActive: {
    backgroundColor: tokens.colors.neutral[800],
  },
  optionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  optionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: 10,
    borderRadius: tokens.borderRadius['3xl'],
    backgroundColor: CropCircleColors.neutral[200],
    gap: 6,
  },
  optionChipSelected: {
    backgroundColor: tokens.colors.neutral[800],
    borderColor: tokens.colors.neutral[800],
  },
  optionChipDisabled: {
    opacity: 0.6,
  },
  optionChipText: {
    ...tokens.typography.label.large,
    fontWeight: tokens.typography.label.largeEmphasised.fontWeight,
    color: tokens.colors.neutral[800],
  },
  optionChipTextSelected: {
    color: tokens.colors.brand.white,
  },
  customDateContainer: { marginTop: tokens.spacing.lg },
  dateRangeContainer: { gap: tokens.spacing.md },
});
